import { useState, useMemo } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';

const ModelUsageTable = ({ modelData, isLoading }) => {
  const [sortField, setSortField] = useState('model');
  const [sortDirection, setSortDirection] = useState('asc');

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 6
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatTime = (value) => {
    if (value === undefined || value === null) {
      return "0.00s";
    }
    return `${value.toFixed(2)}s`;
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <ArrowUp className="inline ml-1" size={12} />
      : <ArrowDown className="inline ml-1" size={12} />;
  };

  const NUMERIC_FIELDS = ['prompts', 'cost', 'avgLatency', 'avgTokens'];

  const sortedRows = useMemo(() => {
    const rows = Object.entries(modelData).map(([model, data]) => ({ model, ...data }));
    return [...rows].sort((a, b) => {
      const valA = a[sortField];
      const valB = b[sortField];
      if (NUMERIC_FIELDS.includes(sortField)) {
        const numA = valA ?? 0;
        const numB = valB ?? 0;
        return sortDirection === 'asc' ? numA - numB : numB - numA;
      }
      const strA = (valA ?? '').toString().toLowerCase();
      const strB = (valB ?? '').toString().toLowerCase();
      if (strA < strB) return sortDirection === 'asc' ? -1 : 1;
      if (strA > strB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [modelData, sortField, sortDirection]);

  const thClass = "px-4 py-3 text-left text-sm font-semibold text-primary uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap";

  return (
    <div className="bg-gray-50 py-4 mb-5">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-base font-semibold text-gray-600 uppercase tracking-wide">Model Usage</h2>
        <div className="text-sm text-gray-500">
          {Object.keys(modelData).length} models in use
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-10">
          <div className="w-3 h-3 border-2 border-t-primary border-primaryLight rounded-full animate-spin mr-2"></div>
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      ) : sortedRows.length > 0 ? (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass} onClick={() => handleSort('model')}>
                  <div className="flex items-center gap-1">Model {renderSortIndicator('model')}</div>
                </th>
                <th className={thClass} onClick={() => handleSort('prompts')}>
                  <div className="flex items-center gap-1">Prompts {renderSortIndicator('prompts')}</div>
                </th>
                <th className={thClass} onClick={() => handleSort('cost')}>
                  <div className="flex items-center gap-1">Cost {renderSortIndicator('cost')}</div>
                </th>
                <th className={thClass} onClick={() => handleSort('avgLatency')}>
                  <div className="flex items-center gap-1">Avg. Latency {renderSortIndicator('avgLatency')}</div>
                </th>
                <th className={thClass} onClick={() => handleSort('avgTokens')}>
                  <div className="flex items-center gap-1">Avg. Tokens {renderSortIndicator('avgTokens')}</div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {sortedRows.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full mr-2 bg-primary"></div>
                      <span className="text-sm font-medium text-gray-900">{row.model}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {formatNumber(row.prompts)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {formatCurrency(row.cost)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {formatTime(row.avgLatency)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {formatNumber(Math.round(row.avgTokens))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No model usage data available for the selected time period
        </div>
      )}
    </div>
  );
};

export default ModelUsageTable;
